import { useEffect, useRef, useState } from "react";
import { Clapperboard, ChevronLeft, ChevronRight, Loader2, Trash2, Video } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { uploadVideoInChunks } from "@/lib/videoUpload";

const MAX_BYTES = 200 * 1024 * 1024;

const StatusBadge = ({ status }) => {
  const { t } = useLang();
  if (status === "ready") return null;
  const failed = status === "failed";
  return (
    <span
      className={`absolute top-2 left-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] font-bold text-white ${
        failed ? "bg-red-600" : "bg-[#0E1A2B]/85"
      }`}
      data-testid="dash-video-status"
    >
      {!failed && <Loader2 className="h-3 w-3 animate-spin" />}
      {failed ? t("dash_video_failed") : t("dash_video_processing")}
    </span>
  );
};

export const DashboardVideos = () => {
  const { t } = useLang();
  const [videos, setVideos] = useState([]);
  const [caption, setCaption] = useState("");
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const res = await fetch(`${API}/owner/videos`, { credentials: "include" });
    if (res.ok) setVideos(await res.json());
  };

  useEffect(() => {
    load();
  }, []);

  const processing = videos.some((v) => v.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [processing]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error(t("dash_video_pick"));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("dash_video_too_big"));
      return;
    }
    setProgress(0);
    try {
      const doc = await uploadVideoInChunks(file, caption, setProgress);
      setVideos((list) => [doc, ...list]);
      setCaption("");
      fileRef.current.value = "";
      toast.success(t("dash_video_queued"));
    } catch {
      toast.error(t("dash_video_fail"));
    } finally {
      setProgress(null);
    }
  };

  const remove = async (v) => {
    const res = await fetch(`${API}/owner/videos/${v.id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) {
      toast.error(t("dash_upload_fail"));
      return;
    }
    setVideos((list) => list.filter((x) => x.id !== v.id));
    toast.success(t("dash_video_removed"));
  };

  const move = async (v, direction) => {
    const res = await fetch(`${API}/owner/videos/${v.id}/move`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ direction }),
    });
    if (res.ok) load();
  };

  return (
    <section className="mb-14" data-testid="dash-videos-section">
      <h2 className="font-display text-2xl font-bold text-white mb-2 flex items-center gap-3">
        <Clapperboard className="h-6 w-6 text-[#F26522]" /> {t("dash_videos")}
      </h2>
      <p className="text-slate-400 text-sm mb-6">{t("dash_videos_sub")}</p>
      <div className="rounded-2xl bg-[#132338] border border-[#203859] p-6 mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <input
          ref={fileRef}
          type="file"
          accept="video/mp4,video/quicktime,video/x-m4v,video/webm,.mp4,.mov,.m4v,.webm"
          className="text-sm text-slate-400 file:mr-4 file:rounded-full file:border-0 file:bg-[#0E1A2B] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#203859] file:cursor-pointer"
          data-testid="dash-video-input"
        />
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={t("dash_caption_ph")}
          className="field-input field-input-dark flex-1"
          data-testid="dash-video-caption-input"
        />
        <button
          onClick={upload}
          disabled={progress !== null}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold px-6 py-3 text-sm transition-[background-color,transform] duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60 whitespace-nowrap"
          data-testid="dash-video-upload-btn"
        >
          <Video className="h-4 w-4" />
          {progress !== null ? `${t("dash_uploading")} ${progress}%` : t("dash_video_upload")}
        </button>
      </div>
      {progress !== null && (
        <div className="h-1.5 w-full rounded-full bg-[#132338] mb-6 overflow-hidden" data-testid="dash-video-progress">
          <div className="h-full bg-[#F26522] transition-[width] duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}
      {videos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dash-videos-grid">
          {videos.map((v) => (
            <div
              key={v.id}
              className="relative group overflow-hidden rounded-2xl border border-[#203859] bg-[#0B1524]"
              data-testid={`dash-video-${v.id}`}
            >
              <div className="aspect-[9/16] max-h-64 w-full flex items-center justify-center">
                {v.poster_path ? (
                  <img src={`${API}/files/${v.poster_path}`} alt={v.caption || "Video"} className="h-full w-full object-cover" />
                ) : (
                  <Video className="h-8 w-8 text-slate-600" />
                )}
              </div>
              <StatusBadge status={v.status} />
              {v.caption && (
                <p className="absolute bottom-0 inset-x-0 bg-[#0E1A2B]/85 px-3 py-2 text-xs text-slate-200 truncate">{v.caption}</p>
              )}
              <div className="absolute top-2 right-2 flex gap-1.5">
                <button onClick={() => move(v, "left")} aria-label={t("dash_move_left")} className="rounded-full bg-[#0E1A2B]/85 p-2 text-slate-300 hover:text-white hover:bg-[#F26522] transition-colors duration-200" data-testid={`dash-video-left-${v.id}`}>
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button onClick={() => move(v, "right")} aria-label={t("dash_move_right")} className="rounded-full bg-[#0E1A2B]/85 p-2 text-slate-300 hover:text-white hover:bg-[#F26522] transition-colors duration-200" data-testid={`dash-video-right-${v.id}`}>
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button onClick={() => remove(v)} aria-label={t("dash_remove_photo")} className="rounded-full bg-[#0E1A2B]/85 p-2 text-slate-300 hover:text-white hover:bg-red-600 transition-colors duration-200" data-testid={`dash-video-delete-${v.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
